import { seedByOvr } from '../matchmaking/bracket';
import { balanceTeams, MAX_BALANCE_POOL } from '../matchmaking/teams';
import type { IdeaCard, RatedPlayer } from '../matchmaking/types';

/**
 * The three one-tap ideas on the finish screen of the combine (RULINGS gaps, gameplay.md "What the
 * app proposes next"): balanced 2v2s, a 1v1 round robin and a bracket. This builds the structure
 * and the facts a card needs. The words come from the client's copy file, so a draft carries no
 * text: `facts` holds the numbers a line can quote.
 *
 * Every idea hands its screen a prefill (`open`), never JSON in a link: games opens /play/new with
 * teams and rules, tournaments opens /tournaments/new with the entries.
 */

export interface IdeaDraft {
  kind: 'balanced_2v2' | 'round_robin_1v1' | 'bracket';
  open: IdeaCard['open'];
  facts: {
    /** Players in the idea. */
    players: number;
    /** Games it makes (round robin and bracket). */
    games?: number;
    /** 2v2: team strengths and the rough chance of team A. */
    strengths?: [number, number];
    winProbA?: number | null;
    /** 2v2: players who sit this one out. */
    sitting?: number;
  };
}

/** The rounds a 1v1 round robin plays up to this many players (15 games for 6). */
export const ROUND_ROBIN_MAX = 6;
export const BRACKET_MAX = 16;

export interface IdeaInput {
  players: readonly RatedPlayer[];
  /** undefined: auto. null: detached. A string: that run. */
  sessionId?: string | null;
}

export function ideaDrafts({ players, sessionId }: IdeaInput): IdeaDraft[] {
  const out: IdeaDraft[] = [];
  const session = sessionId === undefined ? {} : { sessionId };

  if (players.length >= 4) {
    const pool = players.slice(0, MAX_BALANCE_POOL);
    const best = balanceTeams({ players: pool, teamSize: 2, teams: 2, limit: 1 })[0];
    if (best?.teams[0] && best.teams[1]) {
      out.push({
        kind: 'balanced_2v2',
        open: {
          to: 'game',
          prefill: {
            mode: '2v2',
            playerIds: pool.map((p) => p.id),
            teamA: best.teams[0],
            teamB: best.teams[1],
            rulesPreset: 'quick11',
            ...session,
          },
        },
        facts: {
          players: pool.length,
          strengths: [best.strengths[0] ?? 0, best.strengths[1] ?? 0],
          winProbA: best.winProbA,
          sitting: best.sitting.length,
        },
      });
    }
  }

  if (players.length >= 3) {
    const robin = players.slice(0, ROUND_ROBIN_MAX);
    out.push({
      kind: 'round_robin_1v1',
      open: {
        to: 'tournament',
        prefill: {
          format: 'round_robin',
          mode: '1v1',
          rulesPreset: 'iso3',
          entries: robin.map((p) => ({ playerIds: [p.id] })),
          ...session,
        },
      },
      facts: { players: robin.length, games: (robin.length * (robin.length - 1)) / 2 },
    });

    const field = players.slice(0, BRACKET_MAX);
    const seeded = seedByOvr(field.map((p) => ({ id: p.id, ovr: p.ovr })));
    out.push({
      kind: 'bracket',
      open: {
        to: 'tournament',
        prefill: {
          format: 'single_elim',
          mode: '1v1',
          rulesPreset: 'quick11',
          entries: seeded.map((id) => ({ playerIds: [id] })),
          ...session,
        },
      },
      facts: { players: field.length, games: field.length - 1 },
    });
  }
  return out;
}
